import React, { useState } from 'react';
import api from '../utils/api';
import { MessageCircle, X, Send } from 'lucide-react';
import toast from 'react-hot-toast';

function WhatsAppChatModal({ leadId, leadName, mobile, onClose }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    api.get(`/whatsapp/messages/${leadId}`)
      .then(r => setMessages(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [leadId]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post('/whatsapp/send', { leadId, message: text.trim() });
      setMessages(prev => [...prev, {
        id: Date.now(),
        message: text.trim(),
        direction: 'outbound',
        timestamp: Date.now(),
        createdAt: new Date().toISOString()
      }]);
      setText('');
      toast.success('Message sent!');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480, height: '70vh', display: 'flex', flexDirection: 'column', padding: 0 }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ background: '#25d366', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, borderRadius: '12px 12px 0 0' }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'white', fontSize: 16 }}>
            {leadName?.[0]}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: 'white', fontSize: 14 }}>{leadName}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>{mobile}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#f0f2f5', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 20 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>
              No messages yet. Send a message to start the conversation.
            </div>
          ) : messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: m.direction === 'outbound' ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '75%', padding: '8px 12px', borderRadius: m.direction === 'outbound' ? '12px 12px 0 12px' : '12px 12px 12px 0',
                background: m.direction === 'outbound' ? '#dcf8c6' : 'white',
                fontSize: 13, lineHeight: 1.5, boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}>
                <div>{m.message}</div>
                <div style={{ fontSize: 10, color: '#999', marginTop: 4, textAlign: 'right' }}>
                  {new Date(m.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  {m.direction === 'outbound' && ' ✓✓'}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Input */}
        <div style={{ padding: '10px 12px', background: 'white', borderRadius: '0 0 12px 12px', display: 'flex', gap: 8, alignItems: 'center', borderTop: '1px solid var(--border)' }}>
          <input
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
            placeholder="Type a message..."
            style={{ flex: 1, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 20, fontSize: 13, outline: 'none' }}
          />
          <button
            onClick={send}
            disabled={!text.trim() || sending}
            style={{ background: '#25d366', color: 'white', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          >
            {sending ? <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2, borderColor: 'white', borderTopColor: 'transparent' }} /> : <Send size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WhatsAppButton({ leadId, mobile, name, size = 'normal', showLabel = true, label = 'WhatsApp' }) {
  const [showChat, setShowChat] = useState(false);

  const btnStyle = size === 'small' ? {
    background: '#25d366', color: 'white', border: 'none',
    borderRadius: 6, padding: '4px 8px', fontSize: 11, fontWeight: 600,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4
  } : {
    background: '#25d366', color: 'white', border: 'none',
    borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6
  };

  return (
    <>
      <button style={btnStyle} onClick={e => { e.stopPropagation(); setShowChat(true); }}>
        <MessageCircle size={size === 'small' ? 11 : 14} />
        {showLabel && ` ${label}`}
      </button>
      {showChat && (
        <WhatsAppChatModal
          leadId={leadId}
          leadName={name}
          mobile={mobile}
          onClose={() => setShowChat(false)}
        />
      )}
    </>
  );
}
